<p-table
  #expTbl
  [value]="skeletonLoader ? skeletonData : tableData"
  styleClass="experiment-table {{ styleClass }}"
  [(selection)]="selectionModel"
  [rows]="pageSize"
  [columns]="tableColumns"
  [totalRecords]="totalItems"
  [showCurrentPageReport]="true"
  currentPageReportTemplate="{{ firstCount }} - {{ lastCount }} of {{
    totalItems
  }} entries"
  [rowsPerPageOptions]="pagination.options"
  appendTo="body"
  paginatorDropdownAppendTo="body"
  [first]="paginationFirst"
  [showFirstLastIcon]="false"
  scrollDirection="both"
  editMode="row"
  [paginator]="showPagination"
  [showPageLinks]="false"
  [showJumpToPageInput]="false"
  [sortMode]="isClientSide ? 'single' : 'multiple'"
  [customSort]="true"
  (onPage)="onPageChange($event)"
  [scrollable]="scrollFlag"
  [scrollHeight]="scrollHeight"
>
  <!-- Table Header -->
  <ng-template pTemplate="header">
    <tr>
      <th *ngIf="showAccordion" style="width: 40px"></th>
      <ng-container
        *ngFor="let column of tableColumns; let i = index; let lastIndex = last"
      >
        <th
          class="col-minwidth {{ column.value }}-th cursor-pointer"
          [pSortableColumn]="column.sortable ? column.value : ''"
          (click)="
            column.sortable ? toggleSort(column) : $event.stopPropagation()
          "
          *ngIf="!column?.disabled"
          [ngStyle]="{ 'min-width': column?.colMinWidth }"
        >
          <span class="flex items-center justify-start gap-2"
            ><span>{{ column.label }} </span>

            <span class="custom-sort-icon" *ngIf="column.sortable">
              <img
                src="/assets/images/icon-arrow-sort-normal.svg"
                *ngIf="!isSortedColumn(column)"
              />
              <img
                src="/assets/images/icon-arrow-sort.svg"
                *ngIf="isSortedColumn(column)"
                [ngClass]="{
                  asc: isAscending(column),
                  desc: isDescending(column),
                }"
              />
            </span>
          </span>

          <ng-container *ngIf="column?.inputType === 'checkbox'">
            <p-checkbox
              class="experiment-checkbox"
              [binary]="true"
              [(ngModel)]="allSelected"
              (onChange)="toggleAllSelection($event)"
            ></p-checkbox>
          </ng-container>
        </th>
      </ng-container>
    </tr>
    <tr class="filters-row" *ngIf="showFilters">
      <th *ngIf="showAccordion" style="width: 40px"></th>
      <ng-container
        *ngFor="let column of tableColumns; let i = index; let lastIndex = last"
      >
        <th
          *ngIf="column?.value !== 'action' && !column?.disabled"
          class="col-minwidth {{ column.value }}-th"
        >
          <p-columnFilter
            [matchMode]="
              column?.filterType === 'input_text' ? 'contains' : 'equals'
            "
            [showMenu]="false"
            [showClearButton]="false"
            [field]="column.value"
            [ngClass]="'col-minwidth'"
          >
            <ng-template pTemplate="filter" let-filter="filterCallback">
              <input
                *ngIf="column?.filterType === 'input_text'"
                class="filter-input"
                (keyup)="
                  isClientSide
                    ? filterSearchClientSide($any($event.target).value, column)
                    : filterSearch($any($event.target).value, column)
                "
                placeholder="Type here"
                type="text"
                pInputText
              />

              <ng-select
                *ngIf="
                  column?.filterType === 'dropdown' &&
                  column?.filterOptions?.length
                "
                [items]="column.filterOptions"
                [multiple]="true"
                bindLabel="label"
                bindValue="value"
                [(ngModel)]="column.selectedFilterValues"
                placeholder="Select {{
                  column.label ? column.label : 'Option'
                }}"
                [closeOnSelect]="false"
                [searchable]="false"
                [clearable]="false"
                appendTo="body"
                (change)="
                  isClientSide
                    ? onNgSelectChangeClientSide(
                        column.selectedFilterValues,
                        column
                      )
                    : onNgSelectChange(column.selectedFilterValues, column)
                "
                class="filter-dropdown"
              >
                <!-- Custom option template with checkbox -->
                <ng-template
                  ng-option-tmp
                  let-item="item"
                  let-index="index"
                  let-item$="item$"
                >
                  <input type="checkbox" [checked]="item$.selected" readonly />
                  {{ item.label }}
                </ng-template>

                <!-- Override label display to keep placeholder text -->
                <ng-template ng-multi-label-tmp let-items="items">
                  <span class="ng-placeholder"
                    >Select {{ column.label ? column.label : "Option" }}</span
                  >
                </ng-template>
              </ng-select>
            </ng-template>
          </p-columnFilter>
        </th>
      </ng-container>
    </tr>
  </ng-template>

  <!-- Table Body -->
  <ng-template pTemplate="body" let-data let-rowIndex="rowIndex" let-itemRow>
    <!-- Skeleton Loader -->
    <ng-template [ngIf]="skeletonLoader">
      <tr>
        <td *ngFor="let column of tableColumns">
          <p-skeleton height="1rem" styleClass="mb-2"></p-skeleton>
        </td>
      </tr>
    </ng-template>

    <!-- Data Rows -->
    <ng-template [ngIf]="!skeletonLoader">
      <ng-container *ngIf="dragDropEnabled; else nonDraggableRow">
        <tr
          [pSelectableRow]="data"
          pDroppable
          (dragover)="!dragDropDisabled && onRowDragOver($event, data)"
          (onDrop)="!dragDropDisabled && onDropRow($event, data)"
          (click)="!noRowClick ? toSelectedExperiment(data) : ''"
          [ngClass]="{
            'hovered-drop-row': isRowFolderType(data) && data === hoveredRow,
            'disabled-row':
              data?.isRowDisabled ||
              (!isRowFolderType(data) && dragDropDisabled),
            'cursor-pointer': !noRowClick,
          }"
          [ngStyle]="{ 'background-color': data?.rowHighLight }"
        >
          <ng-container
            *ngTemplateOutlet="rowContentTemplate; context: { $implicit: data }"
          ></ng-container>
        </tr>
      </ng-container>

      <ng-template #nonDraggableRow>
        <tr
          [pSelectableRow]="data"
          [ngClass]="{
            'cursor-pointer': dashboardTypes.includes(data?.type),
          }"
          (click)="toSelectedExperiment(data)"
        >
          <td
            *ngIf="showAccordion && data.experiment_type === 'folder'"
            style="width: 40px"
          ></td>

          <td
            *ngIf="showAccordion && data.experiment_type !== 'folder'"
            class="accordion-toggle-cell"
          >
            <button
              class="accordion-toggle"
              (click)="toggleAccordionRow(data, $event)"
            >
              <i
                class="pi text-xs"
                [ngClass]="{
                  'pi-chevron-down': !isRowExpanded(data),
                  'pi-chevron-up': isRowExpanded(data),
                }"
              ></i>
            </button>
          </td>

          <ng-container
            *ngTemplateOutlet="rowContentTemplate; context: { $implicit: data }"
          ></ng-container>
        </tr>

        <!-- Accordion Row -->
        <tr *ngIf="showAccordion && isRowExpanded(data)">
          <!-- Empty cells for the first 2 columns -->
          <td *ngIf="!sibilingTableColSpan" class="w-[34px]"></td>

          <!-- Accordion content spans the middle columns -->
          <td
            [attr.colspan]="
              sibilingTableColSpan
                ? sibilingTableColSpan
                : tableColumns.length - 1
            "
          >
            <div class="accordion-content">
              <ng-container
                *ngTemplateOutlet="
                  accordionTemplate;
                  context: { $implicit: data }
                "
              >
              </ng-container>
            </div>
          </td>

          <td *ngIf="!sibilingTableColSpan" class="w-[34px]"></td>
        </tr>
      </ng-template>
    </ng-template>
  </ng-template>
  <!-- Empty Message -->
  <ng-template pTemplate="emptymessage" let-columns>
    <tr>
      <td
        class="rounded-lg"
        style="text-align: center; padding: 20px 10px"
        [attr.colspan]="(columns?.length || 0) + (showAccordion ? 1 : 0)"
      >
        {{ "PHO.NO_RECORDS_FOUND" | translate }}
      </td>
    </tr>
  </ng-template>
  <ng-template pTemplate="paginatorleft" let-state>
    {{ "PHO.ITEM_PER_PAGE" | translate }}
  </ng-template>

  <!-- Custom paginator template -->
  <ng-template
    pTemplate="paginatorright"
    let-state
    let-onPageChange="onPageChange"
  >
    <div class="custom-paginator">
      <button
        (click)="goToPreviousPage()"
        [disabled]="currentPage === 1"
        class="custom-btn"
      >
        <i class="pi pi-chevron-left"></i>
      </button>

      <span>Page</span>

      <input
        type="number"
        class="page-input"
        [value]="currentPage"
        (change)="onPageInputChange($event)"
        [min]="1"
        [max]="totalPages"
      />

      <span>of {{ totalPages }}</span>

      <button
        (click)="goToNextPage()"
        [disabled]="currentPage === totalPages"
        class="custom-btn"
      >
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>
  </ng-template>
</p-table>

<ng-template #rowContentTemplate let-data>
  <ng-container
    *ngFor="let column of tableColumns; let i = index; let lastIndex = last"
  >
    <td class="col-minwidth {{ column.value }}-td" *ngIf="!column?.disabled">
      <ng-container
        *ngIf="
          isSharedRoute
            ? column?.inputType === 'checkbox' && !data?.isSharedFolder
            : column?.inputType === 'checkbox'
        "
      >
        <p-checkbox
          [binary]="true"
          [inputId]="'checkbox-' + data.id"
          [ngModel]="isClientSide ? data.isChecked : isRowSelected(data)"
          [disabled]="data?.hasAdded === false ? false : data?.hasSynced"
          (onChange)="
            isClientSide
              ? toggleRowSelectionClientSide($event, data)
              : toggleRowSelection($event, data)
          "
        ></p-checkbox>
      </ng-container>

      <ng-container *ngIf="column?.inputType === 'sku_image'">
        <img
          [src]="getSkuImageUrl(data)"
          alt="SKU Image"
          (error)="onSkuImageError($event)"
        />
      </ng-container>

      <!-- Experiment and Folder name column -->
      <ng-container
        *ngIf="
          column.value === 'name' &&
          column.value !== 'action' &&
          column?.inputType !== 'sku_image'
        "
      >
        <span
          class="flex items-center justify-start gap-2"
          [ngClass]="{
            'cursor-pointer': dashboardTypes.includes(data?.type),
          }"
        >
          <div
            pDraggable
            dragEffect="move"
            [draggable]="dragDropEnabled"
            (dragstart)="
              dragDropEnabled && !dragDropDisabled
                ? onDragStart($event, data)
                : $event.stopPropagation()
            "
            class="block max-w-60 items-center justify-start truncate"
            [ngClass]="{
              'cursor-move': dragDropEnabled && !dragDropDisabled,
            }"
            *ngIf="!data.isEditable"
          >
            <img
              src="/assets/images/icon-folder.svg"
              class="mr-1.5"
              *ngIf="
                column.value === 'name' && data?.experiment_type === 'folder'
              "
            />
            {{ getString(data[column.value]) }}
          </div>

          <span class="experiment-name" *ngIf="data.isEditable">
            <input
              type="text"
              styleClass="edit-experiment-name"
              class="form-control edit-experiment-name"
              [(ngModel)]="data.name"
              [ngClass]="{
                'error-experiment-name':
                  data.name.length > experimentNameMaxLength,
              }"
              (click)="$event.stopPropagation()"
            />
          </span>

          <span class="flex gap-2.5" *ngIf="data.isEditable">
            <button
              class="action-btn tick-icon"
              [disabled]="!data.name"
              [ngClass]="{
                'pointer-events-none cursor-not-allowed opacity-50':
                  !data.name || data.name.length > experimentNameMaxLength,
              }"
              (click)="editExperimentAction(data, $event)"
            >
              <img src="/assets/images/tick-icon.svg" /></button
            ><button
              class="action-btn cross-icon"
              (click)="disableEditing(data, $event)"
            >
              <img src="/assets/images/cross-icon.svg" />
            </button>
          </span>

          <span class="flex gap-2.5" *ngIf="column?.hasEdit !== false">
            <ng-container *ngIf="!data?.hasShared">
              <button
                *ngIf="!data.isEditable"
                class="edit-experiment cursor-pointer"
                matTooltip="Edit name"
                (click)="enableEditing(data, $event)"
              >
                <img src="/assets/images/icon-edit-grey.svg" />
              </button>
            </ng-container>

            <button
              *ngIf="!data.isEditable"
              class="open-new-tab edit-experiment cursor-pointer"
              matTooltip="Open in new tab"
              (click)="
                toSelectedExperiment(data, true); $event.stopPropagation()
              "
            >
              <img
                class="table-icons"
                src="/assets/images/icon-navigate-new-tab.svg"
              />
            </button>
          </span>
        </span>
        <div *ngIf="column?.subValue" class="text-[10px] mt-1 text-[#666666]">
          {{ data[column.subValue] }}
        </div>
      </ng-container>

      <!-- Other Column data -->
      <ng-container
        *ngIf="
          column.value !== 'name' &&
          column.value !== 'action' &&
          column.value !== 'status' &&
          column.value !== 'owner_by' &&
          column?.inputType !== 'sku_image' &&
          column.value !== 'processed_status'
        "
      >
        <span
          *ngIf="!column?.isMultipleValue"
          class="flex items-center justify-start gap-2"
          [ngClass]="
            column.value === 'experiment_type' || column.value === 'status'
              ? getString(data[column.value]) + ' capitalize'
              : ''
          "
        >
          <span
            *ngIf="column.value !== 'type'"
            [ngClass]="column.value === 'sku_detail' ? 'block max-w-60' : ''"
          >
            {{
              column.value === "created_at"
                ? formatHumanReadableDate(data[column.value])
                : getString(data[column.value], column)
            }}
          </span>

          <span
            class="badge capitalize"
            [ngClass]="titleAddUnderscore(data[column.value])"
            *ngIf="column.value === 'type'"
          >
            {{ getString(data[column.value], column) }}
          </span>

          <span
            *ngIf="
              column?.isColumnIcon &&
              data[column.value] < column?.isColumnIcon?.minValue
            "
          >
            <app-svg-icon
              [icon]="column.isColumnIcon.icon"
              color="#DC3545"
              padding="10px"
            ></app-svg-icon>
          </span>
        </span>

        <span
          *ngIf="column?.isMultipleValue"
          class="flex items-center justify-start gap-2"
        >
          <ng-container *ngFor="let subKey of column.subValues; let i = index">
            <span class="flex items-center gap-2">
              <span
                class="min-w-[35px] max-w-[48px] justify-center flex gap-1 items-center"
                [ngStyle]="{ color: getValueColor(data[subKey], column, i) }"
              >
                <app-svg-icon
                  class="arrow mt-[2px] {{
                    data[subKey] >= 0 ? 'rotate-up' : 'rotate-down'
                  }}"
                  [color]="getValueColor(data[subKey], column, i)"
                  icon="black-arrow"
                  padding="6px"
                  *ngIf="!column.colorValues?.[i] && data[subKey] != '-'"
                ></app-svg-icon>
                <span>{{
                  getString(
                    data[subKey] != "-"
                      ? getAbsoluteValue(data[subKey])
                      : data[subKey],
                    column
                  )
                }}</span>
                <span *ngIf="!column.colorValues?.[i] && data[subKey] != '-'">{{
                  subKey === "comparison_fsi" ? "" : "%"
                }}</span>
              </span>
              <span
                *ngIf="i < column.subValues.length - 1"
                class="text-[#C0C0C0]"
                >|</span
              >
            </span>
          </ng-container>

          <span
            *ngIf="
              column?.isColumnIcon &&
              data[column.value] < column?.isColumnIcon?.minValue
            "
          >
            <app-svg-icon
              [icon]="column.isColumnIcon.icon"
              color="#DC3545"
              padding="10px"
            ></app-svg-icon>
          </span>
        </span>
      </ng-container>

      <!-- Shared by Column -->
      <ng-container
        *ngIf="this.currentRoute === 'shared' && column.value === 'owner_by'"
      >
        {{ getOwnerName(data?.owner_by) }}
      </ng-container>

      <!-- Status Dropdown Action -->
      <ng-container
        *ngIf="
          !moveDialogView &&
          data.access_type !== 'read' &&
          data.experiment_type !== 'folder' &&
          column.value === 'status'
        "
      >
        <p-dropdown
          [options]="getDropdownOptions(data.status)"
          [(ngModel)]="data.status"
          optionLabel="label"
          optionValue="value"
          [showClear]="false"
          (click)="$event.stopPropagation()"
          (onChange)="statusUpdate($event, data)"
          [disabled]="
            data.access_type === 'read' ||
            needsExtraStatus.includes(data.status)
          "
          styleClass="custom-status-dropdown {{ data.status }}"
        >
          <ng-template pTemplate="selectedItem" let-selectedOption>
            <span class="status-label">
              {{ selectedOption?.label || formatLabel(data.status) }}
            </span>
          </ng-template>
          <ng-template let-option pTemplate="item">
            <span class="status-label flex items-center gap-2">
              <span
                class="w-2 h-2 rounded-full"
                [ngClass]="option.value"
              ></span>
              {{ option.label }}
            </span>
          </ng-template>
        </p-dropdown>
      </ng-container>

      <ng-container
        *ngIf="
          ((moveDialogView || data.access_type === 'read') &&
            data.experiment_type !== 'folder' &&
            column.value === 'status') ||
          column.value === 'processed_status'
        "
      >
        <span class="flex items-center">
          <span
            *ngIf="column.value !== 'processed_status'"
            [ngClass]="data.status + ' no-bg capitalize text-[11px]'"
            >{{ getString(data[column.value], column) }}</span
          >

          <!-- Loader for Bulk Export PDF and Bulk Edit oparation -->
          <ng-container *ngIf="column.value === 'processed_status'">
            <span
              class="w-[22px] mr-1"
              *ngIf="column.value === 'processed_status' && !data?.processed"
              ><img src="/assets/images/icon-plan-processing.gif"
            /></span>

            <span [ngClass]="'no-bg capitalize text-[11px] text-[#333333]'">{{
              getString(data[column.value], column)
            }}</span>
          </ng-container>
        </span>
      </ng-container>

      <!-- Action item -->
      <ng-container
        *ngIf="
          (this.currentRoute !== 'shared' ||
            data.experiment_type !== 'folder') &&
          column.value === 'action'
        "
      >
        <div
          class="flex items-center justify-center"
          *ngIf="!data?.isLoading && column.valueType === 'dot-view'"
        >
          <div
            class="p-1"
            (click)="
              dropdown.toggle($event);
              toggleOverlay(column, data);
              $event.stopPropagation()
            "
          >
            <span>
              <img
                class="table-icons cursor-pointer"
                src="/assets/images/threedots-grey2.svg"
              />
            </span>
          </div>
        </div>

        <div
          class="flex items-center justify-center gap-2"
          *ngIf="!data?.isLoading && column.valueType === 'icon-view'"
        >
          <ng-container *ngFor="let action of column?.actionList">
            <mat-spinner
              class="custom-spinner"
              color="primary"
              mode="indeterminate"
              value="100"
              strokeWidth="2"
              diameter="20"
              *ngIf="action.key === 'add' && data[action.isLoadingKey]"
            >
            </mat-spinner>

            <ng-container
              *ngIf="
                action?.disabledKey
                  ? !action?.disabledValue?.includes(data[action?.disabledKey])
                  : true
              "
            >
              <app-svg-icon
                class="cursor-pointer"
                [ngClass]="{
                  'pointer-events-none opacity-50': data[action.disableKey],
                }"
                [icon]="action.icon"
                [color]="action.color"
                [padding]="action?.padding || '8px'"
                [matTooltip]="action.label"
                (click)="actionCallback(action, data); $event.stopPropagation()"
                *ngIf="
                  action.key === 'add'
                    ? action.enableKey && !data[action?.isLoadingKey]
                    : action?.enableKey
                      ? data[action.enableKey]
                      : true
                "
              ></app-svg-icon>
            </ng-container>
          </ng-container>
        </div>

        <!-- Cloneing progress -->
        <div class="relative" *ngIf="data.isLoading">
          <mat-spinner
            class="custom-spinner"
            color="primary"
            [mode]="mode"
            [value]="data.spinnerPercentage"
            strokeWidth="2"
            diameter="28"
          >
          </mat-spinner>
          <span class="clone-text">{{ data.spinnerPercentage }}%</span>
        </div>
      </ng-container>
    </td>
  </ng-container>
</ng-template>

<p-overlayPanel styleClass="option-dropdown" #dropdown appendTo="body">
  <ng-template pTemplate>
    <div class="container studio-dropdown-wrapper">
      <ng-container *ngFor="let option of actionColumnList">
        <div
          class="dropdown-option py-1 cursor-pointer {{ option.key }}"
          (click)="!option.disable ? experimentAction(option) : ''"
          [ngClass]="{
            'opacity-50 pointer-events-none cursor-not-allowed': option.disable,
          }"
        >
          {{ option.label }}
        </div>
      </ng-container>
    </div>
  </ng-template>
</p-overlayPanel>
